import {isEqual, isNil, noop} from 'lodash';

import {addQueryToPath} from '../../../../libs/shared/helpers/src/browser/urlHelpers';
import {isMobile} from '../../../../libs/shared/helpers/src/environment/mobileEnvHelpers';
import {AsyncStatusesEnum} from '../../../../libs/shared/helpers/src/types/asyncStatuses';
import {makeEnumRuntimeType} from '../../../../libs/shared/helpers/src/typescript/enumHelpers';
import {isDefined} from '../../../../libs/shared/helpers/src/typescript/typeHelpers';
import {ScrollDirectionsEnum} from '../../../../srcCommon/scroll/scrollerTypes';
import {reportToBugsnag} from '../../../../srcCommon/services/bugsnag/bugsnag';
import {
  addPerformanceInteractionMetadata,
  cancelPerformanceInteraction,
  markPerformanceInteractionStep,
  markPerformanceInteractionStepOnAnimationFrame,
} from '../../../../srcCommon/services/performanceMetrics/interactionPerformance';
import {PerformanceInteractionStepsEnum} from '../../../../srcCommon/services/performanceMetrics/interactionPerformanceTypes';
import {getPusherStatus} from '../../../../srcCommon/services/push/pusherStatus';
import {CancelError, CancelToken} from '../../../helpers/asyncCancelHelpers';
import {delayAsync} from '../../../helpers/asyncHelpers';
import {
  CellsBoundary,
  CellsConversationsBoundary,
  CellsMessagesBoundary,
} from '../../../helpers/cellsHelpers';
import {ActionWithPayload, batch} from '../../../helpers/reduxHelpers';
import {ApiConversationCount} from '../../../models/api/conversationCountModel';
import {ConversationHighlightModel} from '../../../models/api/conversationHighlightModel';
import {ConversationMetaModel} from '../../../models/api/conversationMetaModel';
import {ConversationSearchResultModel} from '../../../models/api/conversationSearchResultModel';
import {ConversationUpdatedThinEvent} from '../../../models/api/conversationUpdatedThinEventModel';
import {BackendCountersCheckModel} from '../../../models/api/countersModel';
import {
  ApiConversationPageDataGroup,
  ApiConversationSearchResultPageDataGroup,
  ApiMessagePageDataGroup,
} from '../../../models/api/dataGroupModel';
import {InboxesSearchLocationsEnum, TeammatesMessageFoldersEnum} from '../../../models/url/inboxesUrlModel';
import {fetchApiAsync} from '../../../services/http/http';
import {HttpHeaders, ServerResponseInfo} from '../../../services/http/httpBase';
import {HttpError, HttpNetworkError} from '../../../services/http/httpError';
import {findPanelSequence} from '../../../services/panels';
import {buildPanelCancelToken, findPanelCancelToken} from '../../../services/panelsCancellers';
import {ResponseInfo} from '../../../services/performanceMetrics/legacyPerformanceMiddleware';
import {cellsFetchPerformanceId} from '../../../services/performanceMetrics/performanceIdHelpers';
import {trackEvent, TrackingTypesEnum} from '../../../services/tracking';
import {CallApiFailedAction} from '../../api/apiActions';
import {AppAction} from '../../appActions';
import {AppState} from '../../appState';
import {ThunkAction, ThunkAsyncAction} from '../../appThunk';
import {getCompany} from '../../data/company/companySelectors';
import {
  GetPageConfig,
  makeGetCounter,
  makeGetPageCounterVisibility,
} from '../../data/countersMonitorSelectors';
import {updateDataAction} from '../../data/dataGroupsActions';
import {getComments, getConversations, getOriginalData} from '../../data/dataSelectors';
import {getUserId} from '../../data/teammates/teammatesSelectors';
import {getCanUseSearchRawEndpoint} from '../../featureFlags/featureFlagsSelectors';
import {CellsIdForParameters, CellsListRange} from '../../metadata/cellsLists/cellsList/cellsListState';
import {
  canSyncCellsContext,
  hasFetchedAt,
  MaybeFetchedAt,
  postProcessApiCellsDataGroupFetchedAt,
  sliceCellsConversations,
  sliceCellsConversationsInitial,
  sliceCellsConversationsMore,
  sliceCellsMessages,
  sliceCellsMessagesInitial,
  sliceCellsMessagesMore,
  sliceCellsMessagesOffline,
  sliceCellsMessagesOfflineMore,
} from '../../metadata/cellsLists/cellsListsHelpers';
import {
  hasSearchInInboxesParameters,
  InboxesConversationsParameters,
  InboxesConversationsParametersWithSearch,
  InboxesParameters,
  InboxesParametersTypesEnum,
  InboxesTeammateMessagesParameters,
} from '../inboxes/parameters/inboxesParameters';
import {
  buildParametersFetchQueryContext,
  buildParametersSearchFetchTotalPath,
} from '../inboxes/parameters/inboxesParametersFetchHelpers';
import {
  areParametersCellsEqual,
  computeIsEmptyInboxesSelection,
  findOnlySelectedId,
  InboxesSelection,
} from '../inboxes/parameters/inboxesParametersHelpers';
import {PanelCancelTokenId} from '../panelCancelTokenId';
import {PanelActionPayload, setPanelAction} from '../panelsActions';
import {getCellsPanelPageConversationState, getPanels} from '../panelsSelectors';
import {CellsPanelId, PanelTypesEnum} from '../panelStateBase';
import {buildCellsPanelPage} from './cellsPanelPageHelpers';
import {getCellsPanelContexts, makeGetCellsPanel, makeGetCellsPanelPage} from './cellsPanelSelectors';
import {CellsPanelPageState, CellsPanelPageTypesEnum} from './cellsPanelState';
import {CellsContext, CellsContextTypesEnum} from './context/cellsContext';
import {makeCellsContext} from './context/cellsContextHelpers';
import {
  fetchInitialCellsConversationsFromDataGroup,
  fetchMoreCellsConversationsFromDataGroup,
} from './conversations/cellsPanelConversationsHelpers';
import {
  reportDynamicP2Discrepancies,
  reportDynamicP2MessagesDiscrepancies,
  shouldAcceptConversationUpdatedThinEvent,
  shouldAcceptConversationUpdatedThinEventForUser,
} from './helpers/cellsPanelHelpers';
import {buildCellsPathAndQuery, buildMoreCellsPathAndQuery} from './helpers/cellsPanelPathHelpers';
import {
  fetchInitialCellsMessagesFromDataGroup,
  fetchMoreCellsMessagesFromDataGroup,
} from './messages/cellsPanelMessagesHelpers';
import {
  CellsPanelSearchAction,
  fetchSearchHintsAsyncAction,
  resetSearchResultsCountsAction,
  setSearchResultsCountAction,
} from './search/cellsPanelSearchActions';
import {makeCellsSearchState} from './search/cellsPanelSearchState';

/*
 * Type enum.
 */

export enum CellsPanelActionsEnum {
  NAVIGATED = '@@panels/cells/NAVIGATED',
  P2_DISCREPANCIES_OBSERVED = '@@panels/cells/P2_DISCREPANCIES_OBSERVED',
}

export enum CellsPanelPageActionsEnum {
  PAGE_NAVIGATED = '@@panels/cells/PAGE_NAVIGATED',
  REFETCH_STARTED = '@@panels/cells/REFETCH_STARTED',
  FETCH_CELLS_GRACE_TIMEOUT = '@@panels/cells/FETCH_CELLS_GRACE_TIMEOUT',
}
const RCellsPanelPageAction = makeEnumRuntimeType(CellsPanelPageActionsEnum);

export enum CellsPanelConversationsActionsEnum {
  FETCH_CONVERSATIONS_COMPLETED = '@@panels/cells/FETCH_CONVERSATIONS_COMPLETED',
  FETCH_CONVERSATIONS_FAILED = '@@panels/cells/FETCH_CONVERSATIONS_FAILED',
  FETCH_MORE_CONVERSATIONS = '@@panels/cells/FETCH_MORE_CONVERSATIONS',
  FETCH_MORE_CONVERSATIONS_COMPLETED = '@@panels/cells/FETCH_MORE_CONVERSATIONS_COMPLETED',
  FETCH_MORE_CONVERSATIONS_FAILED = '@@panels/cells/FETCH_MORE_CONVERSATIONS_FAILED',
}
const RCellsPanelConversationsAction = makeEnumRuntimeType(CellsPanelConversationsActionsEnum);

export enum CellsPanelMessagesActionsEnum {
  FETCH_MESSAGES_COMPLETED = '@@panels/cells/FETCH_MESSAGES_COMPLETED',
  FETCH_MESSAGES_FAILED = '@@panels/cells/FETCH_MESSAGES_FAILED',
  FETCH_MORE_MESSAGES = '@@panels/cells/FETCH_MORE_MESSAGES',
  FETCH_MORE_MESSAGES_COMPLETED = '@@panels/cells/FETCH_MORE_MESSAGES_COMPLETED',
  FETCH_MORE_MESSAGES_FAILED = '@@panels/cells/FETCH_MORE_MESSAGES_FAILED',
}
const RCellsPanelMessagesAction = makeEnumRuntimeType(CellsPanelMessagesActionsEnum);

/*
 * Types.
 */

type CellsPanelActionPayload = PanelActionPayload<CellsPanelId>;

interface CellsNavigatedActionPayload extends CellsPanelActionPayload {
  page: CellsPanelPageState;
  cancelTokenId: PanelCancelTokenId;
}
interface CellsNavigatedAction extends ActionWithPayload<CellsNavigatedActionPayload> {
  type: CellsPanelActionsEnum.NAVIGATED;
}

interface CellsPanelPageActionPayload extends CellsPanelActionPayload {
  pageUid: string;
}

interface CellsPageRefetchStartedActionPayload extends CellsPanelPageActionPayload {
  cancelTokenId: PanelCancelTokenId;
}
interface CellsPageRefetchStartedAction extends ActionWithPayload<CellsPageRefetchStartedActionPayload> {
  type: CellsPanelPageActionsEnum.REFETCH_STARTED;
}

interface CellsPageNavigatedActionPayload extends CellsPanelPageActionPayload {
  cellsContext: CellsContext;
  parameters: InboxesParameters;
}
interface CellsPageNavigatedAction extends ActionWithPayload<CellsPageNavigatedActionPayload> {
  type: CellsPanelPageActionsEnum.PAGE_NAVIGATED;
}

interface FetchCellsGraceTimeoutAction extends ActionWithPayload<CellsPanelPageActionPayload> {
  type: CellsPanelPageActionsEnum.FETCH_CELLS_GRACE_TIMEOUT;
}

export enum FetchCellsCachePoliciesEnum {
  /* Don't read or write from cache. */
  NO_CACHE = 'NO_CACHE',
  /* Don't read from cache, but write results from the network to the cache. */
  NETWORK_ONLY = 'NETWORK_ONLY',
  /* Read from the cache, and also fetch from the network to validate. */
  CACHE_THEN_NETWORK = 'CACHE_THEN_NETWORK',
}

export interface FetchCellsContext<T extends InboxesParameters> extends CellsPanelPageActionPayload {
  parameters: T;
  cellsContext: CellsContext;
  cachePolicy: FetchCellsCachePoliciesEnum;
}

interface FetchCellsCompletedContext<T extends InboxesParameters>
  extends FetchCellsContext<T>,
    MaybeFetchedAt<CellsListRange<CellsIdForParameters<T>>> {
  isLoadedFromCache: boolean;
  responseInfo?: ResponseInfo;
}

interface FetchMoreCellsContext<T extends InboxesParameters> extends FetchCellsContext<T> {
  direction: ScrollDirectionsEnum;
  referenceBoundary: CellsBoundary<CellsIdForParameters<T>>;
}

interface FetchMoreCellsCompletedContext<T extends InboxesParameters>
  extends FetchMoreCellsContext<T>,
    FetchCellsCompletedContext<T> {}

/*
 * Types: conversations.
 */

type FetchConversationsContext = FetchCellsContext<InboxesConversationsParameters>;

interface FetchConversationsCompletedContext
  extends FetchConversationsContext,
    FetchCellsCompletedContext<InboxesConversationsParameters> {
  conversationHighlights?: ReadonlyArray<ConversationHighlightModel>;
  conversationMetas?: ReadonlyArray<ConversationMetaModel>;
  conversationSearchResults?: ReadonlyArray<ConversationSearchResultModel>;
  conversationTotal?: number;
}

interface FetchConversationsCompletedAction extends ActionWithPayload<FetchConversationsCompletedContext> {
  type: CellsPanelConversationsActionsEnum.FETCH_CONVERSATIONS_COMPLETED;
}
interface FetchConversationsFailedAction extends CallApiFailedAction<FetchConversationsContext> {
  type: CellsPanelConversationsActionsEnum.FETCH_CONVERSATIONS_FAILED;
}

interface FetchMoreConversationsContext
  extends FetchConversationsContext,
    FetchMoreCellsContext<InboxesConversationsParameters> {}

interface FetchMoreConversationsCompletedContext
  extends FetchMoreConversationsContext,
    FetchMoreCellsCompletedContext<InboxesConversationsParameters> {
  conversationHighlights?: ReadonlyArray<ConversationHighlightModel>;
  conversationMetas?: ReadonlyArray<ConversationMetaModel>;
  conversationSearchResults?: ReadonlyArray<ConversationSearchResultModel>;
}

interface FetchMoreConversationsAction extends ActionWithPayload<FetchMoreConversationsContext> {
  type: CellsPanelConversationsActionsEnum.FETCH_MORE_CONVERSATIONS;
}
interface FetchMoreConversationsCompletedAction
  extends ActionWithPayload<FetchMoreConversationsCompletedContext> {
  type: CellsPanelConversationsActionsEnum.FETCH_MORE_CONVERSATIONS_COMPLETED;
}
interface FetchMoreConversationsFailedAction extends CallApiFailedAction<FetchMoreConversationsContext> {
  type: CellsPanelConversationsActionsEnum.FETCH_MORE_CONVERSATIONS_FAILED;
}

/*
 * Types: messages.
 */

type FetchMessagesContext = FetchCellsContext<InboxesTeammateMessagesParameters>;

interface FetchMessagesCompletedContext
  extends FetchMessagesContext,
    FetchCellsCompletedContext<InboxesTeammateMessagesParameters> {}

interface FetchMessagesCompletedAction extends ActionWithPayload<FetchMessagesCompletedContext> {
  type: CellsPanelMessagesActionsEnum.FETCH_MESSAGES_COMPLETED;
}
interface FetchMessagesFailedAction extends CallApiFailedAction<FetchMessagesContext> {
  type: CellsPanelMessagesActionsEnum.FETCH_MESSAGES_FAILED;
}

interface FetchMoreMessagesContext
  extends FetchMessagesContext,
    FetchMoreCellsContext<InboxesTeammateMessagesParameters> {}

interface FetchMoreMessagesCompletedContext
  extends FetchMoreMessagesContext,
    FetchMoreCellsCompletedContext<InboxesTeammateMessagesParameters> {}

interface FetchMoreMessagesAction extends ActionWithPayload<FetchMoreMessagesContext> {
  type: CellsPanelMessagesActionsEnum.FETCH_MORE_MESSAGES;
}
interface FetchMoreMessagesCompletedAction extends ActionWithPayload<FetchMoreMessagesCompletedContext> {
  type: CellsPanelMessagesActionsEnum.FETCH_MORE_MESSAGES_COMPLETED;
}
interface FetchMoreMessagesFailedAction extends CallApiFailedAction<FetchMoreMessagesContext> {
  type: CellsPanelMessagesActionsEnum.FETCH_MORE_MESSAGES_FAILED;
}

interface P2DiscrepancyObservedActionPayload {
  discrepantConversationIds: ReadonlyArray<number>;
}

interface P2DiscrepancyObservedAction extends ActionWithPayload<P2DiscrepancyObservedActionPayload> {
  type: CellsPanelActionsEnum.P2_DISCREPANCIES_OBSERVED;
}

/*
 * Type guard.
 */

export function isCellsPanelPageAction(action: AppAction): action is CellsPanelPageAction {
  return (
    RCellsPanelPageAction.is(action.type) ||
    RCellsPanelConversationsAction.is(action.type) ||
    RCellsPanelMessagesAction.is(action.type)
  );
}

/*
 * Panel action creator.
 */

export function refetchCellsAsyncAction(panelId: CellsPanelId): ThunkAsyncAction {
  const getCellsPanel = makeGetCellsPanel();
  const getPage = makeGetCellsPanelPage();

  return async (dispatch, getState) => {
    const panel = getCellsPanel(getState(), {panelId});
    const page = getPage(getState(), {panelId});

    // Start a refetch from the network.
    const [cancelTokenId, cancelToken] = buildPanelCancelToken(panelId, panel.cancelTokenId);
    const cachePolicy = FetchCellsCachePoliciesEnum.NETWORK_ONLY;
    const fetchPromise = dispatch(
      fetchCellsAsyncAction(panelId, page.uid, page.parameters, cachePolicy, cancelToken),
    );

    // Update the panel.
    const pageUid = page.uid;
    dispatch({
      type: CellsPanelPageActionsEnum.REFETCH_STARTED,
      payload: {
        panelId,
        pageUid,
        cancelTokenId,
      },
    });

    try {
      await fetchPromise;
    } catch (error) {
      if (error instanceof CancelError) {
        return;
      }

      throw error;
    }
  };
}

export function navigateCellsAsyncAction(
  panelId: CellsPanelId,
  parameters: InboxesParameters,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    // Find the cells panel with this ID.
    const panel = getPanels(getState())[panelId];
    if (panel && panel.type !== PanelTypesEnum.CELLS) {
      throw new Error('Wrong panel type.');
    }

    const cellsContext = makeCellsContext(getState(), parameters);

    // If the panel exists and we don't need a new page, update the existing page.
    const existingPage = panel?.pages.next || panel?.pages.current;
    if (existingPage && !shouldNavigatePanel(existingPage, parameters)) {
      const pageUid = existingPage.uid;

      dispatch<CellsPageNavigatedAction>({
        type: CellsPanelPageActionsEnum.PAGE_NAVIGATED,
        payload: {panelId, pageUid, cellsContext, parameters},
      });
      return;
    }

    // Create the new page to navigate to.
    const page = buildCellsPanelPage(parameters, cellsContext);
    const [cancelTokenId, cancelToken] = buildPanelCancelToken(panelId, panel?.cancelTokenId);
    const cachePolicy = FetchCellsCachePoliciesEnum.CACHE_THEN_NETWORK;

    // Start a fetch for the new page.
    const fetchPromise = dispatch(
      fetchCellsAsyncAction(panelId, page.uid, parameters, cachePolicy, cancelToken),
    );

    // Update or create the panel.
    if (panel) {
      dispatch<CellsNavigatedAction>({
        type: CellsPanelActionsEnum.NAVIGATED,
        payload: {
          panelId,
          page,
          cancelTokenId,
        },
      });
    } else {
      dispatch(
        setPanelAction({
          id: panelId,
          type: PanelTypesEnum.CELLS,
          pages: {current: page, next: undefined},
          search: makeCellsSearchState(),
          cancelTokenId,
          sequence: findPanelSequence(),
        }),
      );
    }

    try {
      await fetchPromise;
    } catch (error) {
      if (error instanceof CancelError) {
        return;
      }

      throw error;
    }
  };
}

/** Check if the new parameters require a new page. */
function shouldNavigatePanel(page: CellsPanelPageState, parameters: InboxesParameters) {
  // If this is a retry, refresh.
  if (page.status === AsyncStatusesEnum.FAILED) {
    return true;
  }

  // If the parameters workspaces are different, always refresh.
  if (!areParametersCellsEqual(page.parameters, parameters)) {
    return true;
  }

  // If the parameters selection is different, maybe refresh.
  if (!isEqual(page.parameters.selection, parameters.selection)) {
    return shouldNavigatePanelForSelection(page, parameters.selection);
  }

  // No need to refresh!
  return false;
}

function shouldNavigatePanelForSelection(
  page: CellsPanelPageState,
  selection: InboxesSelection<number | string>,
) {
  // If the selection is empty, refresh on desktop (the user clicked on the same folder in P1) and do nothing
  // on mobile (the user pressed the Back button in P3).
  if (computeIsEmptyInboxesSelection(selection)) {
    return !isMobile();
  }

  // If no specific ID was requested, no need to refresh.
  const selectedId = findOnlySelectedId(selection);
  if (!selectedId || selectedId === '0') {
    return false;
  }

  // The logic to check for a conversation page and for a message page is different.
  return (
    shouldNavigateConversationsPanel(page, Number(selectedId)) ||
    shouldNavigateMessagesPanel(page, String(selectedId))
  );
}

/** Check if a refresh is needed for a conversation page. */
function shouldNavigateConversationsPanel(page: CellsPanelPageState, selectedId: number) {
  if (page.type !== CellsPanelPageTypesEnum.CONVERSATIONS) {
    return false;
  }

  // If the requested ID matches the page, no need to refresh.
  const parametersSelectedId = findOnlySelectedId(page.parameters.selection);
  if (selectedId === parametersSelectedId) {
    return false;
  }

  // Otherwise, check the actual page content.
  return !page.conversationIds.includes(selectedId);
}

/** Check if a refresh is needed for a message page. */
function shouldNavigateMessagesPanel(page: CellsPanelPageState, selectedId: string) {
  if (page.type !== CellsPanelPageTypesEnum.MESSAGES) {
    return false;
  }

  // If the requested ID matches the page, no need to refresh.
  const parametersSelectedId = findOnlySelectedId(page.parameters.selection);
  if (selectedId === parametersSelectedId) {
    return false;
  }

  // Otherwise, check the actual page content.
  return !page.messageUids.includes(selectedId);
}

/*
 * Fetch action creators.
 */

export function fetchCellsAsyncAction(
  panelId: CellsPanelId,
  pageUid: string,
  parameters: InboxesParameters,
  cachePolicy: FetchCellsCachePoliciesEnum,
  cancelToken: CancelToken,
) {
  const performanceId = cellsFetchPerformanceId.get();

  if (parameters.type === InboxesParametersTypesEnum.TEAMMATE_MESSAGES) {
    return fetchMessagesAsyncAction(panelId, pageUid, parameters, cachePolicy, performanceId, cancelToken);
  }

  return fetchConversationsAsyncAction(panelId, pageUid, parameters, cachePolicy, performanceId, cancelToken);
}

type FrontendCounterInfo =
  | {
      key: string;
      value: number;
      uid?: string;
    }
  | undefined;

function buildCountersValueHeader(counter: FrontendCounterInfo) {
  const headers: HttpHeaders = {};

  if (!counter) {
    return headers;
  }

  if (!isNil(counter.value)) {
    headers['X-Front-Counter-Value'] = String(counter.value);
  }

  if (!isNil(counter.uid)) {
    headers['X-Front-Counter-Uid'] = String(counter.uid);
  }

  return headers;
}

function getCounterValue(context: FetchConversationsContext, state: AppState) {
  const getPageParameters = () => context.parameters;

  const getPageConfig: GetPageConfig = () => ({
    cellsContext: context.cellsContext,
    parameters: context.parameters,
  });

  const getCounterVisibility = makeGetPageCounterVisibility(getPageConfig);
  const counter = makeGetCounter(getPageParameters, getCounterVisibility)(state);
  return counter;
}

export function fetchConversationsAsyncAction(
  panelId: CellsPanelId,
  pageUid: string,
  parameters: InboxesConversationsParameters,
  cachePolicy: FetchCellsCachePoliciesEnum,
  performanceId: string | undefined,
  cancelToken: CancelToken,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const state = getState();
    const company = getCompany(state);
    const canUseSearchRawEndpoint = getCanUseSearchRawEndpoint(getState());

    const pathAndQuery = buildCellsPathAndQuery(state, parameters, canUseSearchRawEndpoint);
    if (!pathAndQuery) {
      cancelPerformanceInteraction(performanceId);
      return;
    }

    if (performanceId) {
      addPerformanceInteractionMetadata(performanceId, 'path_and_query', pathAndQuery);
    }

    const cellsContext = makeCellsContext(state, parameters);

    const context: FetchConversationsContext = {
      panelId,
      pageUid,
      parameters,
      cellsContext,
      cachePolicy,
    };

    // After a grace period, make sure the new page is displayed.
    setTimeout(() => {
      const action: FetchCellsGraceTimeoutAction = {
        type: CellsPanelPageActionsEnum.FETCH_CELLS_GRACE_TIMEOUT,
        payload: context,
      };
      dispatch(action);
    }, 500);

    let counter: FrontendCounterInfo;
    let headers;

    // Being extra safe that this doesn't break any requests as a side effect.
    try {
      // Send the current counter value to the backend. This helps trace incorrect frontend counters.
      counter = getCounterValue(context, state);
      headers = buildCountersValueHeader(counter);
    } catch (error) {
      reportToBugsnag(error);
    }

    try {
      let serverResponseInfo: ServerResponseInfo | undefined;
      const onResponseReceived = (info: ServerResponseInfo) => {
        serverResponseInfo = info;
      };

      const isUsingSearchRawEndpoint = canUseSearchRawEndpoint && hasSearchInInboxesParameters(parameters);

      // Initiate a fetch from the network.
      const fetchDataGroupPromise = fetchApiAsync(
        isUsingSearchRawEndpoint ? ApiConversationSearchResultPageDataGroup : ApiConversationPageDataGroup,
        `${company.url}${pathAndQuery}`,
        {
          cancelToken,
          onResponseReceived,
          headers,
        },
      );

      // Try to fetch from the cache.
      const shouldUseCache = shouldFetchCellsFromCache(cachePolicy, cellsContext);
      const cacheResult = shouldUseCache
        ? sliceCellsConversationsInitial(state, cellsContext, parameters)
        : undefined;
      // Ignore empty cache results, because paginating an empty P2 works but is a weird UX.
      if (shouldUseCache && cacheResult && cacheResult.ids.length > 0) {
        if (performanceId) {
          markPerformanceInteractionStep(performanceId, PerformanceInteractionStepsEnum.CACHE_FETCH);
        }

        // Avoid an unhandled promise rejection warning because we're handling it asynchronously.
        fetchDataGroupPromise.catch(noop);

        // Make sure the UI is updated in response to the FETCH_CONVERSATIONS action, because even rendering
        // cells from cache may take a long time!
        await delayAsync(0, cancelToken);

        dispatch<FetchConversationsCompletedAction>({
          type: CellsPanelConversationsActionsEnum.FETCH_CONVERSATIONS_COMPLETED,
          payload: {
            ...context,
            ...cacheResult,
            isLoadedFromCache: true,
            responseInfo: {
              contentUpdatedAt: 0,
              fetchedAt: cacheResult.fetchedAt,
            },
          },
        });
      }

      if (hasSearchInInboxesParameters(parameters)) {
        // Reset results counts.
        dispatch(resetSearchResultsCountsAction());

        // Retrieve hints if not on mobile.
        if (!isMobile()) {
          dispatch(fetchSearchHintsAsyncAction(parameters, cellsContext, cancelToken));
        }
      }

      // Wait for the fetch from the network.
      const unprocessedFetchDataGroup = await fetchDataGroupPromise;
      if (performanceId) {
        markPerformanceInteractionStep(performanceId, PerformanceInteractionStepsEnum.NETWORK_FETCH);
      }

      const fetchDataGroup = postProcessApiCellsDataGroupFetchedAt(unprocessedFetchDataGroup);

      batch(() => {
        // Update the conversations in the store.
        dispatch(updateDataAction(fetchDataGroup, {shouldNotCascade: true}));

        const updatedState = getState();
        const shouldUseDynamicP2 = canUseDynamicP2(cellsContext);
        const serverFetchResult = fetchInitialCellsConversationsFromDataGroup(
          cellsContext,
          parameters,
          fetchDataGroup,
          (discrepantConversationIds: ReadonlyArray<number>) => {
            dispatch(p2DiscrepancyObservedAction(discrepantConversationIds));
          },
        );
        const fetchResult =
          shouldUseDynamicP2 && hasFetchedAt(serverFetchResult)
            ? // Use our store state to determine the final P2 list.
              sliceCellsConversations(updatedState, cellsContext, parameters, serverFetchResult)
            : // Use the server response.
              serverFetchResult;

        dispatch<FetchConversationsCompletedAction>({
          type: CellsPanelConversationsActionsEnum.FETCH_CONVERSATIONS_COMPLETED,
          payload: {
            ...context,
            ...fetchResult,
            conversationHighlights: fetchDataGroup.conversationHighlights,
            conversationMetas: fetchDataGroup.conversationMetas,
            conversationSearchResults: fetchDataGroup.conversationSearchResults,
            isLoadedFromCache: false,
            responseInfo: serverResponseInfo && {
              ...serverResponseInfo,
              cacheMiss: shouldUseCache && !cacheResult,
            },
          },
        });

        // If we're searching, set the number of results for the current location.
        if (hasSearchInInboxesParameters(parameters) && isDefined(fetchDataGroup.conversationTotal)) {
          dispatch(setSearchResultsCountAction(parameters.search.location, fetchDataGroup.conversationTotal));
        }

        /* Trigger Client-side Counter check when is conversation is done fetching from backend
         * and UI is updated.
         *
         * The frontend "ground" value is the # of conversations rendered in P2.
         * This is taken from the conversationIds field in the redux panel state (state.panels[2].pages.current)
         * There is another field, hiddenConversationId, that causes that conversation not to be rendered in P2
         * even if it is in the conversationIds list, so we have to adjust for it.
         * Note that there is no explcit filter for conversations with teamStatus 'invisible' as they would already
         * have been removed list of conversationIds in the redux panel state.
         */
        const postFetchCompletedState = getState();
        try {
          const counterCheckInfo: BackendCountersCheckModel = JSON.parse(
            serverResponseInfo?.headers['x-front-counter-check'] || '{}',
          );

          const cellsPanelState = getCellsPanelPageConversationState(postFetchCompletedState);
          const feGroundConversationIds =
            cellsPanelState?.conversationIds.filter(
              (conversationId) => conversationId !== cellsPanelState?.hiddenConversationId,
            ) || [];

          const feGround = feGroundConversationIds.length;

          const event = {
            feCounterOnP1ViewClick: counter?.value,
            feCounterOnP2Render: getCounterValue(context, postFetchCompletedState)?.value,
            feGround,
            feGroundConversationIds: feGround <= 50 ? feGroundConversationIds : [],
            beCounter: counterCheckInfo.backendCounter,
            beGround: counterCheckInfo.backendGround,
            counterType: counterCheckInfo.counterType,
            counterView: counterCheckInfo.counterView,
            counterIds: counterCheckInfo.counterIds,
            context: counterCheckInfo.context,
            skipReason: counterCheckInfo.skipReason,
            timeElapsedSinceUidUpdateMs: counterCheckInfo.timeElapsedSinceUidUpdateMs,
            timeElapsedSinceLatestConversationUpdateMs:
              counterCheckInfo.timeElapsedSinceLatestConversationUpdateMs,
            countersPreferences: counterCheckInfo.countersPreferences,
            moreStatusBottom: cellsPanelState?.moreStatusBottom,
            counterUpdateUid: counterCheckInfo.counterUpdateUid,
            rangeLimit: fetchDataGroup.limit,
            pusherStatus: getPusherStatus(),
          };

          if (parameters.search?.query === undefined) {
            trackEvent(TrackingTypesEnum.CLIENT_COUNTERS_CHECK, 'p2-rendered', event);
          }
        } catch (error) {
          reportToBugsnag(error);
        }

        if (shouldUseDynamicP2 && hasFetchedAt(serverFetchResult)) {
          const discrepantConversationIds = reportDynamicP2Discrepancies(
            updatedState,
            cellsContext.teammateId,
            parameters,
            undefined,
            fetchResult.ids,
            serverFetchResult.topBoundary,
            serverFetchResult.bottomBoundary,
            serverFetchResult.ids,
            serverFetchResult.fetchedAt,
          );

          dispatch(p2DiscrepancyObservedAction(discrepantConversationIds));
        }
      });
    } catch (error) {
      cancelPerformanceInteraction(performanceId);

      // If this is a cancellation error, do nothing.
      if (error instanceof CancelError) {
        return;
      }

      // We don't expect errors other than HttpErrors.
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }

      // Otherwise, dispatch the failed action.
      dispatch<FetchConversationsFailedAction>({
        type: CellsPanelConversationsActionsEnum.FETCH_CONVERSATIONS_FAILED,
        payload: {
          ...context,
          error,
        },
      });
    }

    // If we're searching on mobile, we need to retrieve the number of results in the other location.
    if (hasSearchInInboxesParameters(parameters) && isMobile()) {
      const otherLocation = getOtherSearchLocation(parameters.search.location);
      dispatch(fetchConversationsCountAction(parameters, otherLocation, cellsContext, cancelToken));
    }

    markPerformanceInteractionStepOnAnimationFrame(
      performanceId,
      PerformanceInteractionStepsEnum.RENDER,
      true,
    );
  };
}

export function fetchConversationsCountAction(
  parameters: InboxesConversationsParametersWithSearch,
  location: InboxesSearchLocationsEnum,
  cellsContext: CellsContext,
  cancelToken?: CancelToken,
): ThunkAction {
  return async (dispatch, getState) => {
    // Build the search parameters for the desired location.
    const otherSearchParameters = {
      ...parameters.search,
      location,
    };

    // Perform the request.
    try {
      const state = getState();
      const {company} = state.data;
      const context = buildParametersFetchQueryContext(parameters, cellsContext);
      const path = buildParametersSearchFetchTotalPath(parameters, otherSearchParameters, cellsContext);
      const pathAndQuery = addQueryToPath(path, {context});
      const result = await fetchApiAsync(ApiConversationCount, `${company.url}${pathAndQuery}`, {
        cancelToken,
      });

      // Validate the result.
      const {total} = result;

      return dispatch(setSearchResultsCountAction(location, total));
    } catch (error) {
      reportToBugsnag(error);
      return null;
    }
  };
}

function getOtherSearchLocation(location: InboxesSearchLocationsEnum) {
  return location === InboxesSearchLocationsEnum.LOCAL
    ? InboxesSearchLocationsEnum.GLOBAL
    : InboxesSearchLocationsEnum.LOCAL;
}

export function fetchMessagesAsyncAction(
  panelId: CellsPanelId,
  pageUid: string,
  parameters: InboxesTeammateMessagesParameters,
  cachePolicy: FetchCellsCachePoliciesEnum,
  performanceId: string | undefined,
  cancelToken: CancelToken,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const state = getState();
    const company = getCompany(state);
    const pathAndQuery = buildCellsPathAndQuery(state, parameters);
    if (!pathAndQuery) {
      // We won't measure performance for offline fetches.
      cancelPerformanceInteraction(performanceId);

      await dispatch(fetchMessagesOfflineAsyncAction(panelId, pageUid, parameters, cancelToken));
      return;
    }

    if (performanceId) {
      addPerformanceInteractionMetadata(performanceId, 'path_and_query', pathAndQuery);
    }

    const cellsContext = makeCellsContext(state, parameters);

    const context: FetchMessagesContext = {
      panelId,
      pageUid,
      parameters,
      cachePolicy,
      cellsContext,
    };

    // After a grace period, make sure the new page is displayed.
    setTimeout(() => {
      const action: FetchCellsGraceTimeoutAction = {
        type: CellsPanelPageActionsEnum.FETCH_CELLS_GRACE_TIMEOUT,
        payload: context,
      };
      dispatch(action);
    }, 500);

    try {
      let serverResponseInfo: ServerResponseInfo | undefined;
      const onResponseReceived = (info: ServerResponseInfo) => {
        serverResponseInfo = info;
      };

      // Initiate a fetch from the network.
      const fetchDataGroupPromise = fetchApiAsync(ApiMessagePageDataGroup, `${company.url}${pathAndQuery}`, {
        cancelToken,
        onResponseReceived,
      });

      // Try to fetch from the cache.
      const shouldUseCache = shouldFetchCellsFromCache(cachePolicy, cellsContext);
      const cacheResult = shouldUseCache
        ? sliceCellsMessagesInitial(state, cellsContext, parameters)
        : undefined;
      // Ignore empty cache results, because paginating an empty P2 works but is a weird UX.
      if (shouldUseCache && cacheResult && cacheResult.ids.length > 0) {
        if (performanceId) {
          markPerformanceInteractionStep(performanceId, PerformanceInteractionStepsEnum.CACHE_FETCH);
        }

        // Avoid an unhandled promise rejection warning because we're handling it asynchronously.
        fetchDataGroupPromise.catch(noop);

        // Make sure the UI is updated in response to the FETCH_MESSAGES action, because even rendering
        // cells from cache may take a long time!
        await delayAsync(0, cancelToken);

        dispatch<FetchMessagesCompletedAction>({
          type: CellsPanelMessagesActionsEnum.FETCH_MESSAGES_COMPLETED,
          payload: {
            ...context,
            ...cacheResult,
            isLoadedFromCache: true,
            responseInfo: {
              contentUpdatedAt: 0,
              fetchedAt: cacheResult.fetchedAt,
            },
          },
        });
      }

      // Wait for the fetch from the network.
      const unprocessedFetchDataGroup = await fetchDataGroupPromise.catch((error) => {
        // If we are offline and don't have a cache, fall back to an offline result.
        const shouldFetchOffline = shouldUseCache && !cacheResult && shouldFetchCellsOffline(parameters);
        if (error instanceof HttpNetworkError && shouldFetchOffline) {
          dispatch<FetchMessagesCompletedAction>({
            type: CellsPanelMessagesActionsEnum.FETCH_MESSAGES_COMPLETED,
            payload: {
              ...context,
              ...sliceCellsMessagesOffline(state, parameters),
              isLoadedFromCache: true,
              responseInfo: undefined,
            },
          });
        }

        // Re-throw the error; we still want a failed state.
        throw error;
      });
      if (performanceId) {
        markPerformanceInteractionStep(performanceId, PerformanceInteractionStepsEnum.NETWORK_FETCH);
      }

      const fetchDataGroup = postProcessApiCellsDataGroupFetchedAt(unprocessedFetchDataGroup);

      batch(() => {
        // Update the data state.
        dispatch(updateDataAction(fetchDataGroup, {shouldNotCascade: true}));

        const updatedState = getState();
        const allConversations = getConversations(updatedState);
        const allComments = getComments(updatedState);
        const shouldUseDynamicP2 = canSyncCellsContext(cellsContext);

        const serverFetchResult = fetchInitialCellsMessagesFromDataGroup(
          parameters,
          fetchDataGroup,
          allConversations,
          allComments,
        );
        const fetchResult =
          shouldUseDynamicP2 && hasFetchedAt(serverFetchResult)
            ? // Use our store state to determine the final P2 list.
              sliceCellsMessages(updatedState, parameters, serverFetchResult)
            : // Use the server response.
              serverFetchResult;

        dispatch<FetchMessagesCompletedAction>({
          type: CellsPanelMessagesActionsEnum.FETCH_MESSAGES_COMPLETED,
          payload: {
            ...context,
            ...fetchResult,
            isLoadedFromCache: false,
            responseInfo: serverResponseInfo && {
              ...serverResponseInfo,
              cacheMiss: shouldUseCache && !cacheResult,
            },
          },
        });

        if (shouldUseDynamicP2 && hasFetchedAt(serverFetchResult)) {
          reportDynamicP2MessagesDiscrepancies(
            updatedState,
            parameters,
            fetchResult.ids,
            serverFetchResult.topBoundary,
            serverFetchResult.bottomBoundary,
            serverFetchResult.ids,
            serverFetchResult.fetchedAt,
          );
        }
      });
    } catch (error) {
      cancelPerformanceInteraction(performanceId);

      // If this is a cancellation error, do nothing.
      if (error instanceof CancelError) {
        return;
      }

      // We don't expect errors other than HttpErrors.
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }

      // Otherwise, dispatch the failed action.
      dispatch<FetchMessagesFailedAction>({
        type: CellsPanelMessagesActionsEnum.FETCH_MESSAGES_FAILED,
        payload: {
          ...context,
          error,
        },
      });
    }

    markPerformanceInteractionStepOnAnimationFrame(
      performanceId,
      PerformanceInteractionStepsEnum.RENDER,
      true,
    );
  };
}

/** Performs a P2 messages fetch from the store without any network or P2 cache. */
function fetchMessagesOfflineAsyncAction(
  panelId: CellsPanelId,
  pageUid: string,
  parameters: InboxesTeammateMessagesParameters,
  cancelToken: CancelToken,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const state = getState();
    const cellsContext = makeCellsContext(state, parameters);

    const context: FetchMessagesContext = {
      panelId,
      pageUid,
      parameters,
      cachePolicy: FetchCellsCachePoliciesEnum.NO_CACHE,
      cellsContext,
    };

    // Make sure the UI is updated in response to the FETCH_MESSAGES action, because even rendering
    // cells from cache may take a long time!
    await delayAsync(0, cancelToken);

    if (!shouldFetchCellsOffline(parameters)) {
      dispatch<FetchMessagesFailedAction>({
        type: CellsPanelMessagesActionsEnum.FETCH_MESSAGES_FAILED,
        payload: {
          ...context,
          error: new Error('This folder cannot be fetched offline'),
        },
      });
      return;
    }

    dispatch<FetchMessagesCompletedAction>({
      type: CellsPanelMessagesActionsEnum.FETCH_MESSAGES_COMPLETED,
      payload: {
        ...context,
        ...sliceCellsMessagesOffline(state, parameters),
        isLoadedFromCache: true,
        responseInfo: undefined,
      },
    });
  };
}

/*
 * Cache helpers.
 */

function shouldFetchCellsFromCache(cachePolicy: FetchCellsCachePoliciesEnum, cellsContext: CellsContext) {
  return cachePolicy === FetchCellsCachePoliciesEnum.CACHE_THEN_NETWORK && canSyncCellsContext(cellsContext);
}

/*
 * Offline helpers.
 */

/** Indicates whether we can fetch this P2 while offline and with a cold cache. */
function shouldFetchCellsOffline(parameters: InboxesParameters) {
  return (
    parameters.type === InboxesParametersTypesEnum.TEAMMATE_MESSAGES &&
    (parameters.folder === TeammatesMessageFoldersEnum.DRAFTS ||
      parameters.folder === TeammatesMessageFoldersEnum.OUTBOX)
  );
}

/*
 * Fetch more action creators.
 */

export function fetchMoreConversationsAsyncAction(
  panelId: CellsPanelId,
  pageUid: string,
  parameters: InboxesConversationsParameters,
  direction: ScrollDirectionsEnum,
  referenceBoundary: CellsConversationsBoundary,
  cachePolicy: FetchCellsCachePoliciesEnum,
  isUsingSearchRawEndpoint = false,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const state = getState();
    const company = getCompany(state);
    const cellsContext = makeCellsContext(state, parameters);

    const pathAndQuery = buildMoreCellsPathAndQuery(
      state,
      parameters,
      direction,
      referenceBoundary,
      isUsingSearchRawEndpoint,
    );
    if (!pathAndQuery) {
      return;
    }

    const context: FetchMoreConversationsContext = {
      panelId,
      pageUid,
      parameters,
      cellsContext,
      direction,
      referenceBoundary,
      cachePolicy,
    };

    dispatch<FetchMoreConversationsAction>({
      type: CellsPanelConversationsActionsEnum.FETCH_MORE_CONVERSATIONS,
      payload: context,
    });

    try {
      let serverResponseInfo: ServerResponseInfo | undefined;
      const onResponseReceived = (info: ServerResponseInfo) => {
        serverResponseInfo = info;
      };

      // Find the cancel token for this panel.
      const cellsPanel = makeGetCellsPanel()(getState(), {panelId});
      const cancelToken = findPanelCancelToken(cellsPanel.cancelTokenId);

      // Initiate a fetch from the network.
      const fetchDataGroupPromise = fetchApiAsync(
        isUsingSearchRawEndpoint ? ApiConversationSearchResultPageDataGroup : ApiConversationPageDataGroup,
        `${company.url}${pathAndQuery}`,
        {
          cancelToken,
          onResponseReceived,
        },
      );

      // Try to fetch from the cache.
      const shouldUseCache = shouldFetchCellsFromCache(cachePolicy, cellsContext);
      const cacheResult = shouldUseCache
        ? sliceCellsConversationsMore(state, cellsContext, parameters, direction, referenceBoundary)
        : undefined;
      if (shouldUseCache && cacheResult) {
        // Avoid an unhandled promise rejection warning because we're handling it asynchronously.
        fetchDataGroupPromise.catch(noop);

        // Make sure the UI is updated in response to the FETCH_MORE_CONVERSATIONS action, because even rendering
        // cells from cache may take a long time!
        await delayAsync(0, cancelToken);

        dispatch<FetchMoreConversationsCompletedAction>({
          type: CellsPanelConversationsActionsEnum.FETCH_MORE_CONVERSATIONS_COMPLETED,
          payload: {
            ...context,
            ...cacheResult,
            isLoadedFromCache: true,
            responseInfo: {
              contentUpdatedAt: 0,
              fetchedAt: cacheResult.fetchedAt,
            },
          },
        });
      }

      // Wait for the fetch from the network.
      const unprocessedFetchDataGroup = await fetchDataGroupPromise;
      const fetchDataGroup = postProcessApiCellsDataGroupFetchedAt(unprocessedFetchDataGroup);

      batch(() => {
        // Update the conversations in the store.
        dispatch(updateDataAction(fetchDataGroup, {shouldNotCascade: true}));

        const updatedState = getState();
        const shouldUseDynamicP2 = canUseDynamicP2(cellsContext);
        const serverFetchResult = fetchMoreCellsConversationsFromDataGroup(
          cellsContext,
          parameters,
          direction,
          referenceBoundary,
          fetchDataGroup,
          (discrepantConversationIds: ReadonlyArray<number>) => {
            dispatch(p2DiscrepancyObservedAction(discrepantConversationIds));
          },
        );
        const fetchResult =
          shouldUseDynamicP2 && hasFetchedAt(serverFetchResult)
            ? // Use our store state to determine the final P2 list.
              sliceCellsConversations(updatedState, cellsContext, parameters, serverFetchResult)
            : // Use the server response.
              serverFetchResult;

        dispatch<FetchMoreConversationsCompletedAction>({
          type: CellsPanelConversationsActionsEnum.FETCH_MORE_CONVERSATIONS_COMPLETED,
          payload: {
            ...context,
            ...fetchResult,
            conversationHighlights: fetchDataGroup.conversationHighlights,
            conversationMetas: fetchDataGroup.conversationMetas,
            conversationSearchResults: fetchDataGroup.conversationSearchResults,
            isLoadedFromCache: false,
            responseInfo: serverResponseInfo && {
              ...serverResponseInfo,
              cacheMiss: shouldUseCache && !cacheResult,
            },
          },
        });

        if (shouldUseDynamicP2 && hasFetchedAt(serverFetchResult)) {
          const discrepantConversationIds = reportDynamicP2Discrepancies(
            updatedState,
            cellsContext.teammateId,
            parameters,
            referenceBoundary,
            fetchResult.ids,
            serverFetchResult.topBoundary,
            serverFetchResult.bottomBoundary,
            serverFetchResult.ids,
            serverFetchResult.fetchedAt,
          );

          dispatch(p2DiscrepancyObservedAction(discrepantConversationIds));
        }
      });
    } catch (error) {
      // If this is a cancellation error, do nothing.
      if (error instanceof CancelError) {
        return;
      }

      // We don't expect errors other than HttpErrors.
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }

      // Otherwise, dispatch the failed action.
      dispatch<FetchMoreConversationsFailedAction>({
        type: CellsPanelConversationsActionsEnum.FETCH_MORE_CONVERSATIONS_FAILED,
        payload: {
          ...context,
          error,
        },
      });
    }
  };
}

export function fetchMoreMessagesAsyncAction(
  panelId: CellsPanelId,
  pageUid: string,
  parameters: InboxesTeammateMessagesParameters,
  direction: ScrollDirectionsEnum,
  referenceBoundary: CellsMessagesBoundary,
  cachePolicy: FetchCellsCachePoliciesEnum,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const state = getState();
    const company = getCompany(state);
    const cellsContext = makeCellsContext(state, parameters);

    // Build the path & query.
    const pathAndQuery = buildMoreCellsPathAndQuery(state, parameters, direction, referenceBoundary);
    if (!pathAndQuery) {
      return;
    }

    const context: FetchMoreMessagesContext = {
      panelId,
      pageUid,
      parameters,
      cellsContext,
      direction,
      referenceBoundary,
      cachePolicy,
    };

    dispatch<FetchMoreMessagesAction>({
      type: CellsPanelMessagesActionsEnum.FETCH_MORE_MESSAGES,
      payload: context,
    });

    try {
      let serverResponseInfo: ServerResponseInfo | undefined;
      const onResponseReceived = (info: ServerResponseInfo) => {
        serverResponseInfo = info;
      };

      // Find the cancel token for this panel.
      const cellsPanel = makeGetCellsPanel()(getState(), {panelId});
      const cancelToken = findPanelCancelToken(cellsPanel.cancelTokenId);

      // Initiate a fetch from the network.
      const fetchDataGroupPromise = fetchApiAsync(ApiMessagePageDataGroup, `${company.url}${pathAndQuery}`, {
        cancelToken,
        onResponseReceived,
      });

      // Try to fetch from the cache.
      const shouldUseCache = shouldFetchCellsFromCache(cachePolicy, cellsContext);
      const cacheResult = shouldUseCache
        ? sliceCellsMessagesMore(state, cellsContext, parameters, direction, referenceBoundary)
        : undefined;
      if (shouldUseCache && cacheResult) {
        // Avoid an unhandled promise rejection warning because we're handling it asynchronously.
        fetchDataGroupPromise.catch(noop);

        // Make sure the UI is updated in response to the FETCH_MORE_MESSAGES action, because even rendering
        // cells from cache may take a long time!
        await delayAsync(0, cancelToken);

        dispatch<FetchMoreMessagesCompletedAction>({
          type: CellsPanelMessagesActionsEnum.FETCH_MORE_MESSAGES_COMPLETED,
          payload: {
            ...context,
            ...cacheResult,
            isLoadedFromCache: true,
            responseInfo: {
              contentUpdatedAt: 0,
              fetchedAt: cacheResult.fetchedAt,
            },
          },
        });
      }

      // Wait for the fetch from the network.
      const unprocessedFetchDataGroup = await fetchDataGroupPromise.catch((error) => {
        // If we are offline and don't have a cache, fall back to an offline result.
        const shouldFetchOffline = shouldUseCache && !cacheResult && shouldFetchCellsOffline(parameters);
        if (error instanceof HttpNetworkError && shouldFetchOffline) {
          dispatch<FetchMoreMessagesCompletedAction>({
            type: CellsPanelMessagesActionsEnum.FETCH_MORE_MESSAGES_COMPLETED,
            payload: {
              ...context,
              ...sliceCellsMessagesOfflineMore(state, parameters, direction, referenceBoundary),
              isLoadedFromCache: true,
              responseInfo: undefined,
            },
          });
        }

        // Re-throw the error; we still want a failed state.
        throw error;
      });
      const fetchDataGroup = postProcessApiCellsDataGroupFetchedAt(unprocessedFetchDataGroup);

      batch(() => {
        // Update the data state.
        dispatch(updateDataAction(fetchDataGroup, {shouldNotCascade: true}));

        const updatedState = getState();
        const allConversations = getConversations(updatedState);
        const allComments = getComments(updatedState);

        const shouldUseDynamicP2 = canSyncCellsContext(cellsContext);

        const serverFetchResult = fetchMoreCellsMessagesFromDataGroup(
          parameters,
          direction,
          referenceBoundary,
          fetchDataGroup,
          allConversations,
          allComments,
        );
        const fetchResult =
          shouldUseDynamicP2 && hasFetchedAt(serverFetchResult)
            ? // Use our store state to determine the final P2 list.
              sliceCellsMessages(updatedState, parameters, serverFetchResult)
            : // Use the server response.
              serverFetchResult;

        dispatch<FetchMoreMessagesCompletedAction>({
          type: CellsPanelMessagesActionsEnum.FETCH_MORE_MESSAGES_COMPLETED,
          payload: {
            ...context,
            ...fetchResult,
            isLoadedFromCache: false,
            responseInfo: serverResponseInfo && {
              ...serverResponseInfo,
              cacheMiss: shouldUseCache && !cacheResult,
            },
          },
        });

        if (shouldUseDynamicP2 && hasFetchedAt(serverFetchResult)) {
          reportDynamicP2MessagesDiscrepancies(
            updatedState,
            parameters,
            fetchResult.ids,
            serverFetchResult.topBoundary,
            serverFetchResult.bottomBoundary,
            serverFetchResult.ids,
            serverFetchResult.fetchedAt,
          );
        }
      });
    } catch (error) {
      // If this is a cancellation error, do nothing.
      if (error instanceof CancelError) {
        return;
      }

      // We don't expect errors other than HttpErrors.
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }

      // Otherwise, dispatch the failed action.
      dispatch<FetchMoreMessagesFailedAction>({
        type: CellsPanelMessagesActionsEnum.FETCH_MORE_MESSAGES_FAILED,
        payload: {
          ...context,
          error,
        },
      });
    }
  };
}

// TODO: Deprecate this state query posing as a thunk action after we decouple PushContainer from react-redux.
export function computeShouldAcceptConversationUpdatedThinEventAction(
  event: ConversationUpdatedThinEvent,
  shouldAlwaysAcceptForUser: boolean,
): ThunkAction<boolean> {
  return (_dispatch, getState) => {
    const state = getState();
    const dataState = getOriginalData(state);
    const userId = getUserId(state);

    // Maybe accept updates when the user is/was a tracker regardless of context, for P2 caching.
    if (
      shouldAlwaysAcceptForUser &&
      shouldAcceptConversationUpdatedThinEventForUser(dataState, userId, event)
    ) {
      return true;
    }

    return getCellsPanelContexts(state).some((context) =>
      shouldAcceptConversationUpdatedThinEvent(dataState, context.teammateId, context.page, event),
    );
  };
}

/*
 * P2 discrepancies.
 */

export function p2DiscrepancyObservedAction(
  discrepantConversationIds: ReadonlyArray<number>,
): P2DiscrepancyObservedAction {
  return {
    type: CellsPanelActionsEnum.P2_DISCREPANCIES_OBSERVED,
    payload: {
      discrepantConversationIds,
    },
  };
}

/*
 * Combined action type.
 */

type CellsPanelPageAction =
  | CellsPageNavigatedAction
  | CellsPageRefetchStartedAction
  | FetchCellsGraceTimeoutAction
  | FetchConversationsCompletedAction
  | FetchConversationsFailedAction
  | FetchMoreConversationsAction
  | FetchMoreConversationsCompletedAction
  | FetchMoreConversationsFailedAction
  | FetchMessagesCompletedAction
  | FetchMessagesFailedAction
  | FetchMoreMessagesAction
  | FetchMoreMessagesCompletedAction
  | FetchMoreMessagesFailedAction;

export type CellsPanelAction =
  | CellsNavigatedAction
  | CellsPanelPageAction
  | CellsPanelSearchAction
  | P2DiscrepancyObservedAction;

/*
 * Helpers.
 */

function canUseDynamicP2(cellsContext: CellsContext) {
  return (
    cellsContext.type === CellsContextTypesEnum.MIXED_TEAM_TAG ||
    cellsContext.type === CellsContextTypesEnum.CUSTOM_VIEW ||
    canSyncCellsContext(cellsContext)
  );
}
